import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { fadeIn, staggerContainer } from "../variants";
import { X, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";

const images = [
  { id: 1, src: "/modele/modele_4.jpeg", title: "Travaux de toiture", desc: "Remplacement de la charpente par une structure métallique résistante aux intempéries." },
  { id: 2, src: "/modele/modele_5.jpeg", title: "Travaux de toiture", desc: "Remplacement de l'actuelle charpente par tôles galvanisées résistantes aux fortes pluies." },
  { id: 3, src: "/modele/modele_2.jpeg", title: "Fouilles et fondations", desc: "Début des travaux du bâtiment de 2 salles de classe — phase de fouilles." },
  { id: 4, src: "/modele/modele_6.jpeg", title: "Phase élévation", desc: "Construction du bâtiment de 2 salles de classe — phase d'élévation des murs." },
  { id: 5, src: "/modele/modele_7.jpeg", title: "Finition bâtiment", desc: "Finitions du bâtiment de 2 salles de classe." },
  { id: 6, src: "/modele/modele_1.jpeg", title: "Résultat final", desc: "Vue d'ensemble du bâtiment terminé." },
];

const Lightbox = ({ images, index, onClose, onPrev, onNext }) => {
  const img = images[index];
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
        onClick={onClose}
      >
        <button onClick={onClose} className="absolute top-4 right-4 z-10 text-white/70 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors">
          <X size={20} />
        </button>
        <div className="absolute top-4 left-4 z-10 text-white/70 text-sm">
          {index + 1} / {images.length}
        </div>
        <button onClick={(e) => { e.stopPropagation(); onPrev(); }} className="absolute left-4 top-1/2 -translate-y-1/2 z-10 text-white/70 hover:text-white p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors">
          <ChevronLeft size={24} />
        </button>
        <div className="flex flex-col items-center gap-4 max-w-3xl mx-auto px-16" onClick={(e) => e.stopPropagation()}>
          <motion.img
            key={index}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.25 }}
            src={img.src}
            alt={img.title}
            className="max-h-[70vh] max-w-full object-contain rounded-xl shadow-2xl"
          />
          <div className="text-center">
            <div className="text-white font-semibold">{img.title}</div>
            <div className="text-white/60 text-sm mt-1">{img.desc}</div>
          </div>
        </div>
        <button onClick={(e) => { e.stopPropagation(); onNext(); }} className="absolute right-4 top-1/2 -translate-y-1/2 z-10 text-white/70 hover:text-white p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors">
          <ChevronRight size={24} />
        </button>
      </motion.div>
    </AnimatePresence>
  );
};

const ModeleGallery = () => {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const handlePrev = () => setLightboxIndex((i) => (i === 0 ? images.length - 1 : i - 1));
  const handleNext = () => setLightboxIndex((i) => (i === images.length - 1 ? 0 : i + 1));

  React.useEffect(() => {
    const onKey = (e) => {
      if (lightboxIndex === null) return;
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "ArrowRight") handleNext();
      if (e.key === "Escape") setLightboxIndex(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIndex]);

  return (
    <section id="gallery-modele" className="section scroll-mt-20 bg-light">
      <div className="container mx-auto">

        <motion.div
          variants={staggerContainer(0.12, 0.1)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="section-header"
        >
          <motion.p variants={fadeIn("up")} className="eyebrow mb-4">
            Galerie — Modélé
          </motion.p>
          <motion.h2 variants={fadeIn("up", 0.05)} className="h2 mb-6">
            La construction en images
          </motion.h2>
          <motion.p variants={fadeIn("up", 0.1)} className="lead">
            De la première fouille à la remise des clés — suivez la naissance de l'école de Modélé.
          </motion.p>
        </motion.div>

        <motion.div
          variants={staggerContainer(0.08, 0.1)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.1 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
        >
          {images.map((img, i) => (
            <motion.div
              key={img.id}
              variants={fadeIn("up", i * 0.05)}
              className="card group cursor-pointer"
              onClick={() => setLightboxIndex(i)}
            >
              <div className="relative overflow-hidden">
                <img
                  src={img.src}
                  alt={img.title}
                  loading="lazy"
                  className="w-full h-52 object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                  <ZoomIn size={28} className="text-white opacity-0 group-hover:opacity-100 transition-all duration-300 scale-75 group-hover:scale-100" />
                </div>
              </div>
              <div className="p-5">
                <h4 className="font-semibold text-dark text-sm mb-1.5">{img.title}</h4>
                <p className="text-grey text-xs leading-relaxed">{img.desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {lightboxIndex !== null && (
        <Lightbox
          images={images}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onPrev={handlePrev}
          onNext={handleNext}
        />
      )}
    </section>
  );
};

export default ModeleGallery;
